import React, { useState, useEffect } from "react";
import { db, ref, onValue, get } from "./firebase";
import { auth } from "./firebase";
import { Bell } from "lucide-react";
import { DashboardLoading, DashboardNotice, EmptyState, NoCourse } from "./DashboardStates";

// Date formatting helper
const formatDate = (date, options = {}) => {
    if (!date) return '';
    const defaultOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

const Announcements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [enrolledCourse, setEnrolledCourse] = useState(null);

    // Find student's enrolled course
    useEffect(() => {
        const findEnrolledCourse = async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    setLoading(false);
                    return;
                }

                const coursesRef = ref(db, "courses");
                const snapshot = await get(coursesRef);
                let courseIdFound = null;

                if (snapshot.exists()) {
                    const courses = snapshot.val();
                    for (const [courseId, courseData] of Object.entries(courses)) {
                        if (courseData.students) {
                            const student = Object.values(courseData.students).find(
                                (s) => s.email === user.email
                            );
                            if (student) {
                                courseIdFound = courseId;
                                setEnrolledCourse(courseId);
                                break;
                            }
                        }
                    }
                }

                if (!courseIdFound) setLoading(false);
            } catch (err) {
                console.error("Error finding enrolled course:", err);
                setError("Failed to load course information. Please try again.");
                setLoading(false);
            }
        };
        findEnrolledCourse();
    }, []);

    // Fetch announcements for enrolled course
    useEffect(() => {
        if (!enrolledCourse) return;

        const announcementsRef = ref(db, `courses/${enrolledCourse}/announcements`);
        const unsubscribe = onValue(announcementsRef, (snapshot) => {
            try {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const announcementsList = Object.entries(data).map(([id, announcement]) => ({
                        id,
                        ...announcement,
                        date: new Date(announcement.date)
                    })).sort((a, b) => b.date - a.date);
                    setAnnouncements(announcementsList);
                } else {
                    setAnnouncements([]);
                }
                setError(null);
            } catch (err) {
                console.error("Error fetching announcements:", err);
                setError("Failed to load announcements. Please try again.");
            } finally {
                setLoading(false);
            }
        }, (error) => {
            console.error("Error in announcements listener:", error);
            setError("Failed to load announcements. Please try again.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [enrolledCourse]);

    if (loading) return <DashboardLoading />;

    if (!enrolledCourse) {
        return <NoCourse description="Once you're enrolled, course announcements will show up here." />;
    }

    return (
        <div className="px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <h1 className="dash-heading text-2xl md:text-3xl font-bold text-white mb-4 md:mb-0">
                    Announcements
                </h1>
            </div>

            {error && <DashboardNotice message={error} />}

            <div className="space-y-4">
                    {announcements.length > 0 ? (
                        announcements.map((announcement) => (
                            <div
                                key={announcement.id}
                                className="dash-card bg-gray-800/50 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-700/50 hover:border-blue-500/30 transition-all duration-200"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-white">{announcement.title}</h3>
                                    <span className="text-sm font-medium px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                                        {formatDate(announcement.date)}
                                    </span>
                                </div>
                                <p className="text-gray-300 whitespace-pre-wrap">{announcement.content}</p>
                            </div>
                        ))
                    ) : (
                        <EmptyState
                            icon={Bell}
                            title="No announcements yet"
                            description="Class updates, reminders, and exam notes will appear here."
                        />
                    )}
            </div>
        </div>
    );
};

export default Announcements; 